// src/pages/PersonalStatementPage.jsx
//
// PUBLIC landing page for the Personal Statement service (/personal-statement).
// Not auth-gated: prospects and crawlers see it. The crawler-facing copy of this
// page is emitted as static HTML by scripts/prerender.mjs, which imports the SAME
// copy constants from src/config/personalStatement.js -- so the two can't drift.
//
// Visual language: the LIVE public/gold theme (book-* tokens, ornamental-divider,
// decorative-corner cards, Cinzel display / Cormorant serif), matching HomePage.
//
// The indicative price is the ONE editable string (siteText.personalStatement,
// admin Site Text tab -> live, no deploy). It is DISPLAY-ONLY: the real fee is
// quoted per request through the existing serviceRequests flow.
import React from 'react';
import { usePageSeo } from '../components/usePageSeo.js';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth.js';
import { CREDENTIAL_TILES } from '../config/siteCredentials.js';
import {
    PS_EYEBROW, PS_TITLE, PS_LEDE, PS_PROOF_EYEBROW, PS_PROOF_LINE, PS_SERVICES,
    PS_STEPS, PS_AUDIENCE_LINE, PS_AUDIENCE_PILLS, PS_DISCLAIMER,
    PS_PRICE_FALLBACK
} from '../config/personalStatement.js';

export const PersonalStatementPage = ({ state }) => {
    const navigate = useNavigate();
    const { currentUser } = useAuth();

    usePageSeo(state?.siteText, 'personal-statement');

    // Reuses the EXISTING redirect-after-login pattern (AuthGuard/LoginPage read
    // location.state.from): signed out -> signup, then land on the request form.
    const goToRequest = (mode) => {
        const to = `/personal-statement/request?type=${mode}`;
        if (currentUser) navigate(to);
        else navigate('/login?mode=signup', { state: { from: to } });
    };

    // Site Text overrides (Danielle-editable, live) with the shipped constants
    // as fallbacks -- blank field => shipped copy, filled => her text. Mirrors
    // the indicativePrice || fallback pattern below and HomeAiPreview's pick().
    const ps = (state && state.siteText && state.siteText.personalStatement) || {};
    const pick = (v, fallback) => (String(v == null ? '' : v).trim() || fallback);
    const indicativePrice = (ps.indicativePrice || '').trim();
    const feeNote = (ps.feeNote || '').trim();
    // Per-service copy override, keyed by mode (review / consultation).
    const svcCopy = {
        review: { title: ps.reviewTitle, body: ps.reviewBody },
        consultation: { title: ps.consultationTitle, body: ps.consultationBody }
    };

    return (
        <div className="page-fade-in min-h-[calc(100vh-4rem)] bg-book-bg paper-texture">
            <main>
                {/* HERO */}
                <section className="py-16 md:py-24">
                    <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
                        <div className="font-display text-book-accent text-xs mb-4 stagger-item font-semibold">
                            {pick(ps.eyebrow, PS_EYEBROW)}
                        </div>
                        <h1 className="font-display text-3xl md:text-5xl text-book-text leading-tight mb-6 stagger-item" style={{ animationDelay: '0.1s' }}>
                            {pick(ps.title, PS_TITLE)}
                        </h1>
                        <p className="font-serif text-book-muted text-lg leading-relaxed max-w-3xl mx-auto mb-8 stagger-item" style={{ animationDelay: '0.2s' }}>
                            {pick(ps.lede, PS_LEDE)}
                        </p>

                        {/* Indicative price -- blank degrades to the neutral fallback */}
                        <div className="mb-10 stagger-item" style={{ animationDelay: '0.25s' }}>
                            {indicativePrice ? (
                                <>
                                    <div className="font-display text-book-accent text-xs mb-1 font-semibold">Indicative fee</div>
                                    <div className="font-display text-book-accent text-2xl md:text-3xl">{indicativePrice}</div>
                                    {feeNote && <p className="font-serif italic text-book-muted text-sm mt-2">{feeNote}</p>}
                                </>
                            ) : (
                                <p className="font-serif italic text-book-muted text-sm">{PS_PRICE_FALLBACK}</p>
                            )}
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center stagger-item" style={{ animationDelay: '0.3s' }}>
                            <button onClick={() => goToRequest('review')}
                                className="px-5 py-3 bg-book-accent text-black font-display text-sm hover:bg-book-accentLight transition-colors btn-sweep font-semibold">
                                Request a review
                            </button>
                            <button onClick={() => goToRequest('consultation')}
                                className="px-5 py-3 border border-book-accent/50 text-book-accent font-display text-sm hover:border-book-accent hover:bg-book-accent/5 transition-all font-semibold">
                                Book a consultation
                            </button>
                        </div>
                    </div>
                </section>

                {/* PROOF */}
                <section className="py-16 md:py-20 border-t border-book-accent/10 bg-book-pageDark/20">
                    <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
                        <div className="ornamental-divider mb-6 text-book-accent">
                            <h2 className="font-display text-xs font-semibold text-book-muted">{pick(ps.proofEyebrow, PS_PROOF_EYEBROW)}</h2>
                        </div>
                        <p className="font-accent italic text-book-text text-xl md:text-2xl leading-relaxed mb-10">
                            {pick(ps.proofLine, PS_PROOF_LINE)}
                        </p>
                        {/* Same credential tiles as home + dashboard (single source of truth) */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-3xl mx-auto">
                            {CREDENTIAL_TILES.map((s, i) => (
                                <div key={i} className="border border-book-accent/20 p-4 text-center">
                                    <div className="font-display text-book-accent text-xl">{s.n}</div>
                                    <div className="text-book-muted text-xs font-serif italic font-semibold">{s.l}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* SERVICES */}
                <section className="py-20 md:py-28 border-t border-book-accent/10">
                    <div className="max-w-5xl mx-auto px-4 md:px-8">
                        <div className="text-center mb-14">
                            <div className="ornamental-divider mb-6 text-book-accent">
                                <h2 className="font-display text-xs font-semibold text-book-muted">How Danielle Can Help</h2>
                            </div>
                        </div>
                        <div className="grid md:grid-cols-2 gap-5">
                            {PS_SERVICES.map((svc, i) => (
                                <div key={svc.mode}
                                    className="border border-book-accent/20 bg-book-page/40 p-6 md:p-8 decorative-corner text-book-accent stagger-item flex flex-col"
                                    style={{ animationDelay: `${i * 0.08}s` }}>
                                    <h3 className="font-display text-book-accent text-sm mb-4 font-semibold">{pick((svcCopy[svc.mode] || {}).title, svc.title)}</h3>
                                    <p className="font-serif text-book-muted leading-relaxed text-base flex-grow">{pick((svcCopy[svc.mode] || {}).body, svc.body)}</p>
                                    <button onClick={() => goToRequest(svc.mode)}
                                        className="mt-6 self-start px-5 py-2.5 border border-book-accent text-book-accent font-display text-xs hover:bg-book-accent hover:text-black transition-colors font-semibold">
                                        {svc.cta} &rarr;
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* HOW IT WORKS */}
                <section className="py-20 md:py-28 border-t border-book-accent/10 bg-gradient-to-b from-transparent to-book-page/20">
                    <div className="max-w-5xl mx-auto px-4 md:px-8">
                        <div className="text-center mb-14">
                            <div className="ornamental-divider mb-6 text-book-accent">
                                <h2 className="font-display text-xs font-semibold text-book-muted">How It Works</h2>
                            </div>
                        </div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                            {PS_STEPS.map((step, i) => (
                                <div key={i} className="border border-book-accent/20 bg-book-page/30 p-6 text-book-accent stagger-item"
                                    style={{ animationDelay: `${i * 0.08}s` }}>
                                    <div className="font-display text-book-accent text-2xl mb-3">{i + 1}</div>
                                    <div className="h-px w-10 bg-book-accent/40 mb-3"></div>
                                    <p className="font-serif text-book-muted leading-relaxed text-base">{step}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* WHO IT'S FOR */}
                <section className="py-20 md:py-28 border-t border-book-accent/10">
                    <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
                        <div className="ornamental-divider mb-8 text-book-accent">
                            <h2 className="font-display text-xs font-semibold text-book-muted">Who It's For</h2>
                        </div>
                        <p className="font-serif italic text-book-muted text-lg leading-relaxed mb-8">
                            {PS_AUDIENCE_LINE}
                        </p>
                        <div className="flex flex-wrap gap-3 justify-center">
                            {PS_AUDIENCE_PILLS.map((pill, i) => (
                                <span key={i} className="px-4 py-2 border border-book-accent/30 text-book-accent font-display text-xs font-semibold">
                                    {pill}
                                </span>
                            ))}
                        </div>
                    </div>
                </section>

                {/* FINAL CTA + HONEST DISCLAIMER */}
                <section className="py-20 md:py-28 border-t border-book-accent/10">
                    <div className="max-w-3xl mx-auto px-4 md:px-8 text-center">
                        <div className="ornamental-divider mb-8 text-book-accent">
                            <span className="font-display text-book-accent text-xs font-semibold">Ready when you are</span>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
                            <button onClick={() => goToRequest('review')}
                                className="px-5 py-3 bg-book-accent text-black font-display text-sm hover:bg-book-accentLight transition-colors btn-sweep font-semibold">
                                Request a review
                            </button>
                            <button onClick={() => goToRequest('consultation')}
                                className="px-5 py-3 border border-book-accent/50 text-book-accent font-display text-sm hover:border-book-accent hover:bg-book-accent/5 transition-all font-semibold">
                                Book a consultation
                            </button>
                        </div>
                        <p className="font-serif italic text-book-muted text-sm leading-relaxed border-t border-book-accent/15 pt-6">
                            {pick(ps.disclaimer, PS_DISCLAIMER)}
                        </p>
                    </div>
                </section>
            </main>
        </div>
    );
};

export default PersonalStatementPage;
